import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, SearchX, Stethoscope } from "@/lib/public-icons";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/lib/host/auth-client";
import { orpc } from "@/lib/orpc";
import { AlOrderDocument } from "./-components/al-order-document";
import { DocumentHeader } from "@/routes/physician/-components/document-header";
import type { PhysicianDocument } from "@/routes/physician/-components/document-parts-types";
import { PhysicianShell } from "./-components/physician-shell";
import { KIND_LABELS } from "./-components/request-kinds";
import { PlanOfCareDocument } from "./-components/plan-of-care-document";
import { ReferralDocument } from "./-components/referral-document";
import { TherapyNoteDocument } from "./-components/therapy-note-document";

export const Route = createFileRoute("/physician/$requestId")({
  component: RequestPage,
});

function body(document: PhysicianDocument) {
  switch (document.kind) {
    case "physician_plan_of_care":
      return <PlanOfCareDocument document={document} />;
    case "physician_al_order":
      return <AlOrderDocument document={document} />;
    case "therapy_note":
      return <TherapyNoteDocument document={document} />;
    case "therapy_referral":
      return <ReferralDocument document={document} />;
  }
}

/** The document a doctor is being asked to sign, read in full before they sign it. */
function RequestPage() {
  const { requestId } = Route.useParams();
  const session = useSession();
  const enabled = Boolean(session.data);
  const request = useQuery({
    ...orpc.physicianPortal.request.queryOptions({ input: { requestId } }),
    retry: false,
    enabled,
  });
  const document = useQuery({
    ...orpc.physicianPortal.document.queryOptions({ input: { requestId } }),
    retry: false,
    enabled,
  });

  if (!session.data) {
    return (
      <PhysicianShell>
        <PortalNotice
          icon={<Stethoscope />}
          title="Sign in to see this document"
          action={
            <Button asChild size="lg">
              {/* Back to this document once signed in, not the portal's front page. */}
              <Link to="/login" search={{ next: `/physician/${requestId}` }}>
                Sign in
              </Link>
            </Button>
          }
        >
          The community sent it to your physician account.
        </PortalNotice>
      </PhysicianShell>
    );
  }

  if (request.isError) {
    return (
      <PhysicianShell name={session.data.user.name}>
        <PortalNotice
          icon={<SearchX />}
          tone="idle"
          title="This is not here"
          action={
            <Button asChild variant="outline">
              <Link to="/physician">Back to your documents</Link>
            </Button>
          }
        >
          It may have been signed already, or withdrawn. Your queue is on the main page.
        </PortalNotice>
      </PhysicianShell>
    );
  }

  const data = request.data;
  const loaded = document.data;

  return (
    <PhysicianShell name={session.data.user.name}>
      <Link
        className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/80 mb-4 -ml-1 inline-flex min-h-8 items-center gap-1 rounded-md px-1 text-sm outline-hidden focus-visible:ring-[3px] pointer-coarse:min-h-11"
        to="/physician"
      >
        <ChevronLeft className="size-4" />
        Everything waiting for you
      </Link>

      {request.isPending || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="font-display text-2xl text-balance">
              {KIND_LABELS[data.entityType] ?? data.entityType}
            </h1>
            <Badge variant={data.status === "signed" ? "ok" : "outline"}>
              {data.status === "signed" ? "Signed" : "Waiting for you"}
            </Badge>
          </div>

          {document.isPending ? (
            <Skeleton className="h-96 w-full" />
          ) : document.isError || !loaded ? (
            <Card>
              <CardContent className="space-y-2 p-8 text-center">
                <h2 className="font-semibold">This document cannot be shown here</h2>
                <p className="text-muted-foreground text-sm">
                  {errorWords(
                    document.error,
                    "Ask the community to send you a copy before you sign it.",
                  )}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <DocumentHeader resident={loaded.resident} facility={loaded.facility} />
              {body(loaded)}
            </div>
          )}

          {data.status === "pending" ? (
            <Card>
              <CardContent className="p-4">
                <p className="text-muted-foreground text-sm">
                  Signing happens from the list, where you can sign several at once. Your password
                  is asked for there.
                </p>
                <Button asChild className="mt-3">
                  <Link to="/physician">Back to the list</Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </PhysicianShell>
  );
}
